/**
 * {@link DataTable} for partners.
 */
import * as React from 'react';
import { Building2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { DataTable } from '@/components/common/data-table';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { Button } from '@/components/forms/Button';
import { PartnerRow } from './PartnerRow';
import { PartnerCard } from './PartnerCard';
import { PartnerFiltersContent } from './PartnerFilters';
import { renderPartnerDetails } from '@/components/partners/renderPartnerDetails';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { partnersKeys } from '@/hooks/usePartners';
import {
  listPartners,
  getPartnerById,
  deletePartner,
  exportPartnersExcel,
} from '@/services/partners.service';
import { cn } from '@/lib/utils';
import type { Partner } from '@/types/partner.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import type { PartnerFilters } from './partner-configs';
import { hasPartnerActiveFilters, toPartnerListParams } from './partner-table-utils';

/** Filter group IDs for the partners {@link DataTable} configure panel. */
type PartnerFilterGroupId = 'filter-search-partner' | 'filter-city-partner';

/** All ordered filter group ids. */
const ALL_PARTNER_FILTER_GROUP_IDS: readonly PartnerFilterGroupId[] = [
  'filter-search-partner',
  'filter-city-partner',
] as const;

/** Human-readable label per filter group (configure panel). */
const FILTER_GROUP_LABELS: Record<PartnerFilterGroupId, string> = {
  'filter-search-partner': 'Recherche',
  'filter-city-partner': 'Ville',
};

/** Default partner list filters. */
const DEFAULT_PARTNER_FILTERS: PartnerFilters = {
  search: undefined,
  city: undefined,
};

/** Props for {@link PartnersDataTable}. */
export interface PartnersDataTableProps {
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  pageActions?: {
    canView?: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
    canExportExcel?: boolean;
  };
  onView?: (p: Partner) => void;
  onEdit?: (p: Partner) => void;
  isSection?: boolean;
  layoutWidth?: ElementWidth;
  showCreateButton?: boolean;
  onCreate?: () => void;
  toolbarLeft?: React.ReactNode;
}

/** Partners list / grid with filters, details, delete, and Excel export. */
export const PartnersDataTable: React.FC<PartnersDataTableProps> = ({
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  pageActions,
  onView,
  onEdit,
  isSection = false,
  layoutWidth = 'full',
  showCreateButton,
  onCreate,
  toolbarLeft,
}) => {
  const queryClient = useQueryClient();
  const showExport = pageActions?.canExportExcel === true;
  const resolvedView = onView ?? (onToggleRow ? (p: Partner) => onToggleRow(p.id) : undefined);

  const dt = useDataTable<PartnerFilters, PartnerFilterGroupId>({
    tableName: 'partners-partner',
    defaultFilters: DEFAULT_PARTNER_FILTERS,
    allFilterGroupIds: ALL_PARTNER_FILTER_GROUP_IDS,
    layoutWidth,
    isSection,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  const queryParams = React.useMemo(
    () => toPartnerListParams(dt.filters, dt.currentPage, dt.itemsPerPage),
    [dt.filters, dt.currentPage, dt.itemsPerPage],
  );

  const { data: partnersResponse, isLoading } = useQuery({
    queryKey: partnersKeys.list(queryParams),
    queryFn: () => listPartners(queryParams),
  });

  const partners = partnersResponse?.data ?? [];
  const totalItems = partnersResponse?.total ?? 0;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  /** Fetch the expanded row when it is not on the current page. */
  const expandedPartnerId = dt.expandedRowId ?? '';
  const {
    data: expandedPartnerData,
    isLoading: isLoadingExpandedPartner,
    isError: isExpandedPartnerError,
    error: expandedPartnerError,
  } = useQuery({
    queryKey: partnersKeys.detail(expandedPartnerId),
    queryFn: () => getPartnerById(expandedPartnerId),
    enabled: !!dt.expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedPartnerError,
    error: expandedPartnerError,
    catalogKey: 'partnerNotFound',
    onClose: onCloseDetails,
    entityId: dt.expandedRowId,
  });

  const expandedItem: Partner | null = expandedPartnerData ?? null;

  const hasActiveFilters = React.useMemo(() => hasPartnerActiveFilters(dt.filters), [dt.filters]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePartner(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['partners'] });
      dt.setDeleteId(null);
      toast.success('Partenaire supprimé avec succès !');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const partnerToDelete = React.useMemo(
    () => partners.find((p) => p.id === dt.deleteId) ?? null,
    [partners, dt.deleteId],
  );

  const [isExporting, setIsExporting] = React.useState(false);
  const isEmptyForExport = !isLoading && totalItems === 0;

  /** Excel export of the current filter set (blocked when the list is empty). */
  const handleExportExcel = React.useCallback(async () => {
    if (isEmptyForExport) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await exportPartnersExcel(toPartnerListParams(dt.filters));
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [dt.filters, isEmptyForExport]);

  const setSearch = React.useCallback(
    (v: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, search: v }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setCity = React.useCallback(
    (v: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, city: v }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const filtersContent = React.useMemo(
    () => (
      <PartnerFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onSetSearch={setSearch}
        onSetCity={setCity}
      />
    ),
    [dt.filters, dt.allowedFilterIds, dt.visibleFilterIds, setSearch, setCity],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  const clearFilters = React.useCallback(() => {
    dt.setFilters(DEFAULT_PARTNER_FILTERS);
    dt.setCurrentPage(1);
  }, [dt]);

  return (
    <DataTable
      data={partners}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="partenaires"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={6}
      toolbarActions={
        <>
          <Button
            preset="toolbar-filter"
            className={cn(dt.showFilters && 'section-action-button-active')}
            showBadge={hasActiveFilters}
            onClick={() => {
              const next = !dt.showFilters;
              dt.setShowFilters(next);
              if (!next) dt.setShowConfigure(false);
            }}
          />
          {showExport && (
            <Button
              preset="toolbar-export"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isEmptyForExport}
              tooltipText={
                isEmptyForExport && !isExporting
                  ? 'Aucun partenaire à exporter.'
                  : 'Exporter en Excel'
              }
            />
          )}
          {showCreateButton && onCreate && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer un partenaire"
              onClick={() => {
                onCloseDetails?.();
                onCreate();
              }}
            />
          )}
        </>
      }
      filtersPanel={
        <DataTableFilters
          show={dt.showFilters}
          hasActiveFilters={hasActiveFilters}
          filterGroups={filterGroups}
          onClearFilters={clearFilters}
          resultCount={totalItems}
          resultLabel="partenaire(s)"
          onToggleConfigure={
            dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
          }
          showConfigure={dt.showConfigure}
          configureContent={
            <div className="flex flex-wrap gap-3">
              {ALL_PARTNER_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map(
                (id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn
                          ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                          : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                      )}
                    >
                      {FILTER_GROUP_LABELS[id]}
                    </Button>
                  );
                },
              )}
            </div>
          }
        />
      }
      renderTableHead={() => (
        <tr>
          <th>Nom</th>
          <th>Ville</th>
          <th>Site web</th>
          <th>Contact</th>
          <th>Type</th>
          <th className="w-28 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(partner) => (
        <PartnerRow
          partner={partner}
          pageActions={pageActions}
          onView={resolvedView}
          onEdit={onEdit}
          onDelete={(id) => dt.setDeleteId(id)}
        />
      )}
      renderCard={(partner) => (
        <PartnerCard
          partner={partner}
          pageActions={pageActions}
          onView={resolvedView}
          onEdit={onEdit}
          onDelete={(id) => dt.setDeleteId(id)}
        />
      )}
      emptyIcon={Building2}
      emptyText="Aucun partenaire trouvé"
      title="Partenaires"
      sidebarMenuId="menu-partners"
      isSection={isSection}
      toolbarLeft={toolbarLeft}
      expandedRowId={dt.expandedRowId}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      onCloseDetails={onCloseDetails}
      expandedItem={expandedItem}
      isExpandedDetailLoading={isLoadingExpandedPartner}
      renderExpandedRow={(partner) => renderPartnerDetails(partner)}
      renderDetailsContent={(partner) => renderPartnerDetails(partner)}
      modals={
        <DeleteModal
          open={!!dt.deleteId}
          onClose={() => dt.setDeleteId(null)}
          onConfirm={() => {
            if (dt.deleteId) deleteMutation.mutate(dt.deleteId);
          }}
          itemName={partnerToDelete ? `"${partnerToDelete.name}"` : undefined}
          isLoading={deleteMutation.isPending}
        />
      }
    />
  );
};
